import { useTranslation } from "react-i18next";
import type { AnalysisEnvelope, RiskFlag, WatchItem } from "../api/types";
import { clockTime, percent } from "../components/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { directionTone, toneTextClass } from "../components/BiasBadge";
import { cn } from "@/lib/utils";

interface AnalysisExpansionProps {
  analysis: AnalysisEnvelope;
  onClusterJump: (clusterId: string) => void;
}

function formatCost(value: number | undefined): string | null {
  if (value == null) return null;
  return `$${value.toFixed(4)}`;
}

function severityBadgeClass(severity: string): string {
  // WatchItem and RiskFlag severity strings come from the analysis body and
  // aren't constrained to HIGH/MED/LOW. Map common values to Tailwind palette;
  // fall back to neutral.
  const v = severity.toLowerCase();
  if (v.includes("high") || v.includes("critical")) {
    return "bg-destructive text-destructive-foreground";
  }
  if (v.includes("med") || v.includes("warn")) {
    return "bg-amber-500 text-amber-950";
  }
  return "bg-secondary text-secondary-foreground";
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold text-primary mb-2">
      <span className="inline-block h-3 w-0.5 bg-primary rounded-sm" aria-hidden="true" />
      {children}
    </h3>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return <div className="text-sm text-muted-foreground">{children}</div>;
}

function WatchItemRow({ item }: { item: WatchItem }) {
  return (
    <article className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0 flex-1">
        <strong className="text-sm">{item.item}</strong>
        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
          <Badge className={cn("text-xs", severityBadgeClass(item.severity))}>{item.severity}</Badge>
          <span>{item.category} · {item.horizon}</span>
        </div>
      </div>
    </article>
  );
}

function RiskFlagRow({ risk }: { risk: RiskFlag }) {
  return (
    <article className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0 flex-1">
        <strong className="text-sm">{risk.flag}</strong>
        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
          <Badge className={cn("text-xs", severityBadgeClass(risk.severity))}>{risk.severity}</Badge>
          <span>{risk.horizon}</span>
        </div>
      </div>
    </article>
  );
}

export function AnalysisExpansion({ analysis, onClusterJump }: AnalysisExpansionProps) {
  const { t } = useTranslation("dashboard");
  const body = analysis.body;

  if (!body) {
    return (
      <div className="px-4 pb-4 text-sm text-muted-foreground">
        {t("expansion.noBody")}
      </div>
    );
  }

  const inputTokens = analysis.llm_input_tokens;
  const outputTokens = analysis.llm_output_tokens;
  const cost = formatCost(analysis.llm_cost_usd);
  const generatedAt = analysis.generated_at || body.generated_at;

  return (
    <div className="px-4 pb-4 space-y-4 border-t border-border pt-3">
      <section>
        <SectionHeading>{t("expansion.keyDrivers")}</SectionHeading>
        {body.key_drivers.length > 0 ? (
          <div className="divide-y divide-border">
            {body.key_drivers.map((driver, index) => {
              const tone = directionTone(`${driver.direction} ${driver.driver}`);
              return (
                <article
                  className="flex items-start justify-between gap-3 py-2"
                  key={`${driver.supporting_cluster_id}-${index}`}
                >
                  <div className="min-w-0 flex-1">
                    <strong className="text-sm">{driver.driver}</strong>
                    <div className="text-xs mt-0.5 flex items-center gap-2">
                      <span className={cn("font-medium", toneTextClass(tone))}>
                        {driver.direction}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">
                        {t("expansion.weight", { value: percent(driver.weight) })}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs shrink-0"
                    onClick={() => onClusterJump(driver.supporting_cluster_id)}
                    type="button"
                  >
                    {t("expansion.jumpToCluster")}
                  </Button>
                </article>
              );
            })}
          </div>
        ) : (
          <EmptyState>{t("expansion.noDrivers")}</EmptyState>
        )}
      </section>

      <section>
        <SectionHeading>{t("expansion.watchItems")}</SectionHeading>
        {body.watch_items.length > 0 ? (
          <div className="divide-y divide-border">
            {body.watch_items.map((item, index) => (
              <WatchItemRow item={item} key={`${item.item}-${index}`} />
            ))}
          </div>
        ) : (
          <EmptyState>{t("expansion.noWatch")}</EmptyState>
        )}
      </section>

      <section>
        <SectionHeading>{t("expansion.riskFlags")}</SectionHeading>
        {body.risk_flags.length > 0 ? (
          <div className="divide-y divide-border">
            {body.risk_flags.map((risk, index) => (
              <RiskFlagRow risk={risk} key={`${risk.flag}-${index}`} />
            ))}
          </div>
        ) : (
          <EmptyState>{t("expansion.noRisks")}</EmptyState>
        )}
      </section>

      <section>
        <SectionHeading>{t("expansion.caveats")}</SectionHeading>
        {body.confidence_caveats.length > 0 ? (
          <ul className="list-disc list-inside text-sm space-y-1">
            {body.confidence_caveats.map((caveat, index) => (
              <li key={`${caveat}-${index}`}>{caveat}</li>
            ))}
          </ul>
        ) : (
          <EmptyState>{t("expansion.noCaveats")}</EmptyState>
        )}
      </section>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs pt-2 border-t border-border">
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t("expansion.meta.model")}</dt>
          <dd className="font-mono truncate">{analysis.model || body.model}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t("expansion.meta.generated")}</dt>
          <dd className="font-mono">
            <time dateTime={generatedAt} title={clockTime(generatedAt)}>
              {generatedAt}
            </time>
          </dd>
        </div>
        {inputTokens != null ? (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t("expansion.meta.inputTokens")}</dt>
            <dd className="font-mono">{inputTokens.toLocaleString()}</dd>
          </div>
        ) : null}
        {outputTokens != null ? (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t("expansion.meta.outputTokens")}</dt>
            <dd className="font-mono">{outputTokens.toLocaleString()}</dd>
          </div>
        ) : null}
        {cost ? (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t("expansion.meta.cost")}</dt>
            <dd className="font-mono">{cost}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}
